/**
 * Posición de Pixel en pantalla: su lugar fijo sobre «Cerrar sesión», límites al arrastrar, hacia
 * dónde abrir el panel, el globo y la nube de pensamiento según el espacio libre,
 * y hacia dónde mirar para seguir al cursor.
 * Funciones puras (sin DOM) para poder probarlas con jest.
 */

export const LAUNCHER_SIZE = 73;
const EDGE = 12;
const GAP = 8;
const MIN_POPOVER_HEIGHT = 220;

/** Distancia a la que, al soltarlo, Pixel vuelve solo a su lugar del menú. */
const DOCK_SNAP = 56;

/**
 * Lugar de Pixel dentro del hueco reservado en el menú lateral, arriba de «Cerrar sesión».
 * rect: { left, top, width, height } del hueco. Devuelve null si el hueco no está a la
 * vista (menú oculto, cajón de móvil cerrado o pie del menú fuera de la pantalla).
 */
export function dockPosition(rect, vw, vh) {
    if (!rect || rect.width < LAUNCHER_SIZE * 0.9 || rect.height < 1) return null;
    if (rect.left < 0 || rect.top < 0 || rect.left + rect.width > vw || rect.top + rect.height > vh + 1) return null;
    return {
        x: Math.round(rect.left + (rect.width - LAUNCHER_SIZE) / 2),
        y: Math.round(rect.top + (rect.height - LAUNCHER_SIZE) / 2),
    };
}

/**
 * Dónde espera Pixel cuando su hueco no está a la vista: en móvil abajo a la derecha;
 * en escritorio abajo, pegado al borde derecho del menú (o a la orilla si está oculto),
 * para no tapar los botones del menú.
 */
export function restPosition({ narrow, sideRight, vw, vh }) {
    if (narrow) return defaultPosition(vw, vh);
    return clampPosition({ x: Math.max(0, sideRight || 0) + 16, y: vh - LAUNCHER_SIZE - 16 }, vw, vh);
}

/** true si Pixel quedó lo bastante cerca de su hueco como para volver a él. */
export function nearDock(pos, dock) {
    if (!pos || !dock) return false;
    return Math.hypot(pos.x - dock.x, pos.y - dock.y) <= DOCK_SNAP;
}

/** Esquina inferior derecha: su lugar en pantallas angostas. */
export function defaultPosition(vw, vh) {
    return clampPosition({ x: vw - LAUNCHER_SIZE - 16, y: vh - LAUNCHER_SIZE - 16 }, vw, vh);
}

/** Mantiene a la mascota completa dentro de la ventana. */
export function clampPosition(pos, vw, vh) {
    const maxX = Math.max(0, vw - LAUNCHER_SIZE);
    const maxY = Math.max(0, vh - LAUNCHER_SIZE);
    return {
        x: Math.round(Math.min(maxX, Math.max(0, Number(pos?.x) || 0))),
        y: Math.round(Math.min(maxY, Math.max(0, Number(pos?.y) || 0))),
    };
}

/**
 * Coloca un panel/globo junto a la mascota: arriba si la mascota está en la mitad
 * inferior, abajo si está en la superior; alineado al lado con más espacio.
 * Con `clearLeft` (borde derecho del menú lateral cuando Pixel está encima de él) se abre
 * a un lado, fuera del menú, para no tapar sus botones.
 * Devuelve estilos fijos { left, width, top | bottom, maxHeight } en px.
 */
export function placePopover({ pos, vw, vh, width, maxHeight, clearLeft = null }) {
    if (clearLeft != null && clearLeft + Math.min(width, vw - EDGE * 2) + EDGE <= vw) {
        const bottom = Math.max(EDGE, vh - (pos.y + LAUNCHER_SIZE));
        return {
            left: clearLeft,
            width: Math.min(width, vw - EDGE * 2),
            maxHeight: Math.max(MIN_POPOVER_HEIGHT, Math.min(maxHeight, vh - bottom - EDGE)),
            above: true,
            alignRight: false,
            beside: true,
            bottom,
        };
    }
    const above = pos.y + LAUNCHER_SIZE / 2 > vh / 2;
    const room = above
        ? pos.y - GAP - EDGE
        : vh - (pos.y + LAUNCHER_SIZE) - GAP - EDGE;
    const w = Math.min(width, vw - EDGE * 2);
    const alignRight = pos.x + LAUNCHER_SIZE / 2 > vw / 2;
    const rawLeft = alignRight ? pos.x + LAUNCHER_SIZE - w : pos.x;
    const left = Math.max(EDGE, Math.min(vw - w - EDGE, rawLeft));
    const placed = {
        left,
        width: w,
        maxHeight: Math.max(MIN_POPOVER_HEIGHT, Math.min(maxHeight, room)),
        above,
        alignRight,
    };
    if (above) placed.bottom = vh - pos.y + GAP;
    else placed.top = pos.y + LAUNCHER_SIZE + GAP;
    return placed;
}

/* Centro de la pantalla-cara dentro del botón de 73 px (sprite de 50 unidades a 1.4 px). */
export const EYE_OFFSET = { x: 36, y: 29 };

/**
 * Dirección de la mirada según la distancia del cursor a la cara:
 * x en -2..2 (lejos = 2) y y en -1..1, con una zona muerta para no temblar.
 */
export function gazeFor(dx, dy) {
    const x = Math.abs(dx) < 36 ? 0 : Math.sign(dx) * (Math.abs(dx) < 260 ? 1 : 2);
    const y = Math.abs(dy) < 36 ? 0 : Math.sign(dy);
    return { x, y };
}

/**
 * Lado de la nube de pensamiento: hacia el centro de la pantalla; si Pixel está
 * pegado arriba, la nube baja para no salirse.
 */
export function thoughtPlacement(pos, vw) {
    return {
        side: pos.x + LAUNCHER_SIZE / 2 < vw / 2 ? 'right' : 'left',
        low: pos.y < 36,
    };
}
